import React, { useState, useEffect } from "react";

interface Provider {
  id: number;
  first_name: string;
  last_name: string;
  role: "doctor" | "technician";
  email: string;
}

interface Equipment {
  id: number;
  name: string;
  type: "laser" | "EMS" | "Therapy" | "other";
  model?: string;
  serial_number?: string;
  status: "available" | "maintenance" | "broken" | "retired";
  purchase_date?: string;
  purchase_cost?: number;
  location?: string;
  notes?: string;
  owner: { id: number; first_name: string; last_name: string };
  operator?: { id: number; first_name: string; last_name: string };
}

interface EquipmentFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  editingEquipment?: Equipment | null;
  providers: Provider[];
}

const EquipmentForm: React.FC<EquipmentFormProps> = ({
  isOpen,
  onClose,
  onSave,
  editingEquipment,
  providers,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    type: "other" as "laser" | "EMS" | "Therapy" | "other",
    model: "",
    serial_number: "",
    owner_id: "",
    operator_id: "",
    status: "available" as "available" | "maintenance" | "broken" | "retired",
    purchase_date: "",
    purchase_cost: "",
    location: "",
    notes: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  // Color theme
  const colorTheme = {
    primary: "#667eea",
    success: "#10b981",
    error: "#ef4444",
    gray50: "#f9fafb",
    gray100: "#f3f4f6",
    gray200: "#e5e7eb",
    gray500: "#6b7280",
    gray600: "#4b5563",
    gray900: "#1f2937",
    cardBg: "#ffffff",
  };

  const API_BASE =
    process.env.REACT_APP_API_URL ||
    "https://dr-bassam-elayoubi.caduceuslb.com/api";

  // Equipment types with icons
  const equipmentTypes = [
    { value: "laser", label: "Laser Equipment", icon: "🔴" },
    { value: "EMS", label: "Electrical Stimulation", icon: "⚡" },
    { value: "Therapy", label: "Therapy", icon: "💆" },
    { value: "other", label: "Other Equipment", icon: "🌀" },
  ];

  // Equipment status options
  const statusOptions = [
    { value: "available", label: "Available", color: "#10b981" },
    { value: "maintenance", label: "Under Maintenance", color: "#f59e0b" },
    { value: "broken", label: "Broken", color: "#ef4444" },
    { value: "retired", label: "Retired", color: "#6b7280" },
  ];

  // Initialize form when editing
  useEffect(() => {
    if (editingEquipment) {
      setFormData({
        name: editingEquipment.name,
        type: editingEquipment.type,
        model: editingEquipment.model || "",
        serial_number: editingEquipment.serial_number || "",
        owner_id: editingEquipment.owner.id.toString(),
        operator_id: editingEquipment.operator?.id.toString() || "",
        status: editingEquipment.status,
        purchase_date: editingEquipment.purchase_date || "",
        purchase_cost: editingEquipment.purchase_cost?.toString() || "",
        location: editingEquipment.location || "",
        notes: editingEquipment.notes || "",
      });
    } else {
      // Reset form for new equipment
      setFormData({
        name: "",
        type: "other",
        model: "",
        serial_number: "",
        owner_id: "",
        operator_id: "",
        status: "available",
        purchase_date: "",
        purchase_cost: "",
        location: "",
        notes: "",
      });
    }
    setErrors({});
  }, [editingEquipment, isOpen]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      const method = editingEquipment ? "PUT" : "POST";
      const url = editingEquipment
        ? `${API_BASE}/equipment/${editingEquipment.id}`
        : `${API_BASE}/equipment`;

      // Prepare data
      const submitData = {
        ...formData,
        purchase_cost: formData.purchase_cost
          ? parseFloat(formData.purchase_cost)
          : null,
        operator_id: formData.operator_id || null,
      };

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        onSave();
        onClose();
      } else {
        const errorData = await response.json();
        if (errorData.errors) {
          setErrors(errorData.errors);
        } else {
          setErrors({ submit: errorData.error || "Failed to save equipment" });
        }
      }
    } catch (error) {
      console.error("Error saving equipment:", error);
      setErrors({ submit: "Network error. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "20px",
      }}
    >
      <div
        style={{
          background: colorTheme.cardBg,
          borderRadius: "16px",
          maxWidth: "700px",
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "24px 32px",
            borderBottom: `1px solid ${colorTheme.gray200}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h3
              style={{
                fontSize: "20px",
                fontWeight: "600",
                color: colorTheme.gray900,
                margin: "0 0 4px 0",
              }}
            >
              {editingEquipment ? "Edit Equipment" : "Add New Equipment"}
            </h3>
            <p
              style={{
                color: colorTheme.gray500,
                fontSize: "14px",
                margin: 0,
              }}
            >
              Configure equipment details and ownership
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              border: "none",
              background: colorTheme.gray100,
              color: colorTheme.gray500,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: "32px" }}>
          {/* Equipment Name & Type */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px",
              marginBottom: "24px",
            }}
          >
            <div>
              <label
                htmlFor="Equipment"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: colorTheme.gray600,
                  marginBottom: "8px",
                }}
              >
                Equipment Name{" "}
                <span style={{ color: colorTheme.error }}>*</span>
              </label>
              <input
                type="text"
                id="equipment_name"
                name="equipment_name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${
                    errors.name ? colorTheme.error : colorTheme.gray200
                  }`,
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
                placeholder="Enter equipment name..."
                required
              />

              {errors.name && (
                <p
                  style={{
                    color: colorTheme.error,
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.name}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="Equipment"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: colorTheme.gray600,
                  marginBottom: "8px",
                }}
              >
                Equipment Type{" "}
                <span style={{ color: colorTheme.error }}>*</span>
              </label>
              <select
                value={formData.type}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    type: e.target.value as any,
                  }))
                }
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${
                    errors.type ? colorTheme.error : colorTheme.gray200
                  }`,
                  borderRadius: "8px",
                  fontSize: "14px",
                  background: colorTheme.cardBg,
                }}
                required
              >
                {equipmentTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.icon} {type.label}
                  </option>
                ))}
              </select>
              {errors.type && (
                <p
                  style={{
                    color: colorTheme.error,
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.type}
                </p>
              )}
            </div>
          </div>

          {/* Model & Serial Number */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px",
              marginBottom: "24px",
            }}
          >
            <div>
              <label
                htmlFor="Model"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: colorTheme.gray600,
                  marginBottom: "8px",
                }}
              >
                Model
              </label>
              <input
                type="text"
                id="equipment_model"
                name="equipment_model"
                value={formData.model}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, model: e.target.value }))
                }
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${colorTheme.gray200}`,
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
                placeholder="Equipment model..."
              />
            </div>

            <div>
              <label
                htmlFor="Serial"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: colorTheme.gray600,
                  marginBottom: "8px",
                }}
              >
                Serial Number
              </label>
              <input
                type="text"
                id="serial_number"
                name="serial_number"
                value={formData.serial_number}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    serial_number: e.target.value,
                  }))
                }
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${
                    errors.serial_number ? colorTheme.error : colorTheme.gray200
                  }`,
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
                placeholder="Serial number..."
              />

              {errors.serial_number && (
                <p
                  style={{
                    color: colorTheme.error,
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.serial_number}
                </p>
              )}
            </div>
          </div>

          {/* Sponser & Operator */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px",
              marginBottom: "24px",
            }}
          >
            <div>
              <label
                htmlFor="Sponser"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: colorTheme.gray600,
                  marginBottom: "8px",
                }}
              >
                Sponser <span style={{ color: colorTheme.error }}>*</span>
              </label>
              <select
                value={formData.owner_id}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, owner_id: e.target.value }))
                }
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${
                    errors.owner_id ? colorTheme.error : colorTheme.gray200
                  }`,
                  borderRadius: "8px",
                  fontSize: "14px",
                  background: colorTheme.cardBg,
                }}
                required
              >
                <option value="">Select owner...</option>
                {providers.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.first_name} {provider.last_name} ({provider.role})
                  </option>
                ))}
              </select>
              {errors.owner_id && (
                <p
                  style={{
                    color: colorTheme.error,
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.owner_id}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="Operator"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: colorTheme.gray600,
                  marginBottom: "8px",
                }}
              >
                Operator
              </label>
              <select
                value={formData.operator_id}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    operator_id: e.target.value,
                  }))
                }
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${colorTheme.gray200}`,
                  borderRadius: "8px",
                  fontSize: "14px",
                  background: colorTheme.cardBg,
                }}
              >
                <option value="">Select operator...</option>
                {providers.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.first_name} {provider.last_name} ({provider.role})
                  </option>
                ))}
              </select>
              <p
                style={{
                  color: colorTheme.gray500,
                  fontSize: "12px",
                  marginTop: "4px",
                }}
              >
                Who operates this equipment (optional)
              </p>
            </div>
          </div>

          {/* Status & Location */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px",
              marginBottom: "24px",
            }}
          >
            <div>
              <label
                htmlFor="Status"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: colorTheme.gray600,
                  marginBottom: "8px",
                }}
              >
                Status <span style={{ color: colorTheme.error }}>*</span>
              </label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    status: e.target.value as any,
                  }))
                }
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${colorTheme.gray200}`,
                  borderRadius: "8px",
                  fontSize: "14px",
                  background: colorTheme.cardBg,
                }}
                required
              >
                {statusOptions.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="Location"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: colorTheme.gray600,
                  marginBottom: "8px",
                }}
              >
                Location
              </label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, location: e.target.value }))
                }
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${colorTheme.gray200}`,
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
                placeholder="Room or location..."
              />
            </div>
          </div>

          {/* Purchase Details */}
          <div
            style={{
              background: colorTheme.gray50,
              borderRadius: "12px",
              padding: "20px",
              marginBottom: "24px",
            }}
          >
            <h4
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: colorTheme.gray900,
                marginBottom: "16px",
              }}
            >
              Purchase Information
            </h4>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
              }}
            >
              <div>
                <label
                  htmlFor="Purchase"
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: colorTheme.gray600,
                    marginBottom: "6px",
                  }}
                >
                  Purchase Date
                </label>
                <input
                  type="date"
                  id="purchase_date"
                  name="purchase_date"
                  value={formData.purchase_date}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      purchase_date: e.target.value,
                    }))
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: `2px solid ${colorTheme.gray200}`,
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div>
                <label
                  htmlFor="Purchase"
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: colorTheme.gray600,
                    marginBottom: "6px",
                  }}
                >
                  Purchase Cost
                </label>
                <input
                  type="number"
                  id="purchase_cost"
                  name="purchase_cost"
                  value={formData.purchase_cost}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      purchase_cost: e.target.value,
                    }))
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: `2px solid ${colorTheme.gray200}`,
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: "24px" }}>
            <label
              htmlFor="Notes"
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: colorTheme.gray600,
                marginBottom: "8px",
              }}
            >
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, notes: e.target.value }))
              }
              rows={3}
              style={{
                width: "100%",
                padding: "12px",
                border: `2px solid ${colorTheme.gray200}`,
                borderRadius: "8px",
                fontSize: "14px",
                resize: "none",
              }}
              placeholder="Additional notes about the equipment..."
            />
          </div>

          {/* Error Display */}
          {errors.submit && (
            <div
              style={{
                padding: "12px 16px",
                background: "#fecaca",
                border: "1px solid #fca5a5",
                borderRadius: "8px",
                color: "#991b1b",
                fontSize: "14px",
                marginBottom: "24px",
              }}
            >
              {errors.submit}
            </div>
          )}

          {/* Form Actions */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              paddingTop: "20px",
              borderTop: `1px solid ${colorTheme.gray200}`,
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "12px 24px",
                border: `1px solid ${colorTheme.gray200}`,
                borderRadius: "8px",
                background: colorTheme.cardBg,
                color: colorTheme.gray600,
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: "12px 24px",
                border: "none",
                borderRadius: "8px",
                background: loading ? colorTheme.gray200 : colorTheme.success,
                color: "white",
                fontSize: "14px",
                fontWeight: "600",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading
                ? "Saving..."
                : editingEquipment
                ? "Update Equipment"
                : "Create Equipment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EquipmentForm;
